import React from 'react';
import ServicePageTemplate from './ServicePageTemplate';
import { Accessibility, ShieldCheck, Users, Repeat, MapPin, HeartPulse } from 'lucide-react';

const WheelchairTransportation = () => {
  const seo = {
    title: 'Wheelchair Transportation Richmond TX, Sugar Land & Greater Houston | MoveraCare',
    description: 'Wheelchair transportation serving Richmond TX, Sugar Land, Missouri City, Katy, Fort Bend County, and Greater Houston. Wheelchair-accessible vehicles with proper securement; drivers assist passengers during transport. Call 832-925-2128.',
    path: '/services/wheelchair-transportation',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Wheelchair Transportation',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: [
        { '@type': 'City', name: 'Richmond, TX' },
        { '@type': 'City', name: 'Sugar Land, TX' },
        { '@type': 'City', name: 'Missouri City, TX' },
        { '@type': 'City', name: 'Katy, TX' },
        { '@type': 'City', name: 'Rosenberg, TX' },
        { '@type': 'City', name: 'Stafford, TX' },
        { '@type': 'City', name: 'Fulshear, TX' },
        { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
        { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
      ],
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        legalName: 'MoveraCare Services LLC',
        telephone: '+1-832-925-2128',
        email: 'dispatch@moveracaremobility.com',
        url: 'https://www.moveracaremobility.com',
        address: { '@type': 'PostalAddress', addressLocality: 'Richmond', addressRegion: 'TX', addressCountry: 'US' },
      },
    },
  };

  return (
    <ServicePageTemplate
      testid="service-wheelchair"
      seo={seo}
      eyebrow="WHEELCHAIR TRANSPORTATION · RICHMOND, TX · FORT BEND COUNTY"
      heading="Wheelchair Transportation in Richmond TX, Sugar Land, and Greater Houston"
      intro="MoveraCare Mobility is based in Richmond, TX and provides non-emergency wheelchair transportation in wheelchair-accessible vehicles for medically stable passengers across Fort Bend County, Harris County, and Greater Houston — including Richmond, Sugar Land, Missouri City, Katy, Rosenberg, Stafford, and Fulshear. Drivers assist passengers during transport; wheelchair type, weight, and any equipment needs are confirmed at booking so dispatch can assess the trip."
      who={{
        title: 'Designed for Wheelchair Users and the People Who Support Them',
        bullets: [
          'Patients using manual or power wheelchairs',
          'Families arranging wheelchair transportation for a loved one',
          'Hospital and SNF discharge planners',
          'Dialysis and rehabilitation coordinators',
          'Workers’ Compensation case managers and adjusters',
          'Community organizations referring wheelchair passengers',
        ],
      }}
      included={{
        title: 'Wheelchair Transportation Includes',
        bullets: [
          'Wheelchair-accessible vehicle',
          'Manual wheelchair transportation',
          'Power wheelchair transportation (subject to vehicle capacity and advance confirmation)',
          'Proper wheelchair securement',
          'Drivers assist passengers during transport',
          'Door-to-door or curb-to-curb assistance coordinated at booking',
          'Round-trip or one-way transportation',
          'Recurring wheelchair transportation schedules',
          'Wait-and-return coordination when arranged in advance',
        ],
      }}
      howItWorks={{
        title: 'From Request to Ride',
        steps: [
          { title: 'Trip Request', desc: 'Provide passenger name, mobility level, wheelchair type, weight, any equipment needs (including oxygen), pickup, destination, and appointment time.' },
          { title: 'Confirmation', desc: 'MoveraCare confirms whether we can safely serve the trip, assigns a wheelchair-accessible vehicle, and confirms the pickup window when the trip is accepted.' },
          { title: 'Pickup', desc: 'Driver arrives, assists the passenger during transport, and secures the wheelchair per standard procedures.' },
          { title: 'Delivery', desc: 'Passenger is transported to the medical destination with door-to-door or curb-to-curb assistance coordinated at booking.' },
        ],
      }}
      why={{
        title: 'Why Choose MoveraCare for Wheelchair Transportation',
        items: [
          { icon: Accessibility, title: 'Wheelchair-Accessible Vehicles', desc: 'Wheelchair-accessible vehicles with proper wheelchair securement.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport. Mobility and equipment needs are confirmed at booking.' },
          { icon: Repeat, title: 'Recurring Scheduling', desc: 'Reliable standing wheelchair transportation for treatment schedules.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Operations', desc: 'Professional handling of passenger information across dispatch and operations.' },
          { icon: HeartPulse, title: 'Healthcare-Focused', desc: 'Coordination designed around medical appointments and healthcare workflows.' },
          { icon: MapPin, title: 'Richmond &amp; Fort Bend Coverage', desc: 'Based in Richmond, TX. Verified service area across Fort Bend County, Harris County, Sugar Land, Missouri City, Katy, Rosenberg, Stafford, Fulshear, and Greater Houston.' },
        ],
      }}
      faqs={[
        { question: 'Can you transport power wheelchairs?', answer: 'Yes, subject to the wheelchair’s dimensions, weight, and vehicle compatibility. Please share wheelchair type and specifications when requesting the trip.' },
        { question: 'How far in advance should I schedule wheelchair transportation?', answer: 'We recommend at least 24 hours’ notice. Same-day requests may be considered based on availability.' },
        { question: 'Do you provide door-to-door assistance?', answer: 'Door-to-door or curb-to-curb assistance is coordinated at booking depending on passenger needs, property layout, and the confirmed service arrangement. Drivers assist passengers during transport.' },
        { question: 'Is wait-and-return service available?', answer: 'Yes. Wait-and-return service can be arranged when requested and confirmed in advance. Wait-time policies and charges apply.' },
      ]}
    />
  );
};

export default WheelchairTransportation;
